import { useState } from 'react';
import { useI18n } from 'core/i18n/I18nProvider';
import { publicSpecialistView, shouldPresentAssignedSpecialist, specialistInitials } from 'features/consultations/consultationUtils';
import { safeAvatarUrl } from 'shared/utilities/format';

export function AssignedSpecialistCard({
  consultation,
  specialist: specialistProp,
  compact = false,
  headingLevel = compact ? 'p' : 'h2',
}) {
  const { t } = useI18n();
  const specialist = publicSpecialistView(specialistProp || consultation?.specialist);
  const showSection = specialistProp
    ? Boolean(specialist)
    : shouldPresentAssignedSpecialist(consultation);

  if (!showSection) {
    return null;
  }

  if (!specialist) {
    return (
      <p className="muted specialist-unavailable" data-testid="specialist-unavailable">
        {t('consultations.specialistUnavailable')}
      </p>
    );
  }

  return (
    <SpecialistPortrait specialist={specialist} compact={compact} headingLevel={headingLevel} />
  );
}

function SpecialistPortrait({ specialist, compact, headingLevel: Heading }) {
  const { t } = useI18n();
  const [imageFailed, setImageFailed] = useState(false);
  const name = specialist.name || t('consultations.specialistUnavailable');
  const title = specialist.title || '';
  const avatarUrl = imageFailed ? null : safeAvatarUrl(specialist.avatar_url);
  const languages = (specialist.languages || []).filter((code) => code === 'ar' || code === 'en');
  const initials = specialistInitials(specialist.name);
  const alt = specialist.name
    ? t('consultations.specialistPhoto', { name: specialist.name })
    : t('consultations.specialistPhotoFallback');

  return (
    <article
      className={`specialist-card${compact ? ' specialist-card-compact' : ''}`}
      data-testid="assigned-specialist"
    >
      {avatarUrl ? (
        <img
          className="specialist-avatar"
          src={avatarUrl}
          alt={alt}
          width={compact ? 40 : 56}
          height={compact ? 40 : 56}
          loading={compact ? 'lazy' : 'eager'}
          decoding="async"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="specialist-avatar specialist-avatar-fallback" data-testid="specialist-avatar-fallback" aria-hidden="true">
          {initials}
        </span>
      )}
      <div className="specialist-copy">
        <Heading className="specialist-name">{name}</Heading>
        {title ? <p className="specialist-title">{title}</p> : null}
        {!compact && languages.length ? (
          <p className="muted specialist-languages">
            {languages.map((code) => t(`language.${code}`)).join(' · ')}
          </p>
        ) : null}
        {!compact && specialist.timezone ? (
          <p className="muted specialist-timezone">{specialist.timezone}</p>
        ) : null}
      </div>
    </article>
  );
}
